/**
 * Fallback for a StaleBanner rendered WITHOUT `ann` (SheetWorkspace's edit-mode banner as it is
 * today): the banner then borrows the actions ("Skoða og staðfesta", "Fela lög af eldri útgáfu")
 * of the mounted LayerPanel, which provides them here. The most recently mounted provider wins (one
 * sheet is edited at a time). A banner given `ann` (works in view mode too) does not need this; once
 * every StaleBanner gets `ann`, this module and LayerPanel's provideStaleActions call can go.
 */
import { useSyncExternalStore } from 'react';

export interface StaleActions {
  /** Own layers drawn on an older revision (the "Skoða og staðfesta" action is offered when > 0). */
  ownCount: number;
  /** Opens the review sheet for all own stale layers. */
  review(): void;
  /** Hides every stale layer. */
  hideAll(): void;
}

const providers: { actions: StaleActions }[] = [];
const listeners = new Set<() => void>();

function emit(): void {
  for (const fn of listeners) fn();
}

/** Registers actions; returns the unregister function (use as a useEffect cleanup). */
export function provideStaleActions(actions: StaleActions): () => void {
  const slot = { actions };
  providers.push(slot);
  emit();
  return () => {
    const i = providers.indexOf(slot);
    if (i >= 0) providers.splice(i, 1);
    emit();
  };
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** The actions of the most recently mounted provider (null when none). */
export function currentStaleActions(): StaleActions | null {
  return providers[providers.length - 1]?.actions ?? null;
}

/** The actions of the mounted layer panel, or null when none is mounted. */
export function useStaleActions(): StaleActions | null {
  return useSyncExternalStore(subscribe, currentStaleActions, () => null);
}
